import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';

import {
  BehaviorSubject,
  distinctUntilChanged,
  Observable,
  Subject,
  Subscription,
  debounceTime,
  map,
  take,
  withLatestFrom,
} from 'rxjs';
import { IconLibrary } from '@finnairoyj/fcom-ui-styles/enums';

import { compareAsJson, unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { Category, FinnairServiceItem, SeatmapSvgAndControlData } from '@fcom/dapi/api/models';
import { getIconForServiceCategory } from '@fcom/dapi/utils';

import { getInitials, getSeatInfoForTracking, getSeatMapFlight, servicesForFlightAndTravelerId } from '../../../utils';
import { FlightWithStreams, SeatMapTraveler } from '../../../interfaces';

const DEFAULT_ICON_LIBRARY = IconLibrary.SVG_LIBRARY;

@Component({
  selector: 'fin-seat-map-renderer',
  styleUrls: ['./seat-map-renderer.component.scss'],
  templateUrl: './seat-map-renderer.component.html',
  standalone: false,
})
export class SeatMapRendererComponent implements AfterViewInit, OnDestroy {
  @Input() flight: FlightWithStreams;
  @Input() passengers$: Observable<SeatMapTraveler[]>;
  @Input() unavailableSeatLabel: string;
  @Input() includedSeats: FinnairServiceItem | null;

  @Output() updatePosition: EventEmitter<'left' | 'center' | 'right'> = new EventEmitter();
  @Output() selectSeat: EventEmitter<{ passengerId: string; seatNumber: string; exitRow: boolean }> =
    new EventEmitter();
  @Output() updateScrollPosition: EventEmitter<number> = new EventEmitter();

  @ViewChild('seatMap') seatMap: ElementRef;
  @ViewChild('seatMapWrapper') seatMapWrapper: ElementRef;

  activePax$: Observable<SeatMapTraveler>;
  scrollPositionWatcher$ = new BehaviorSubject<'left' | 'center' | 'right'>('left');
  subscriptions: Subscription = new Subscription();
  templates: Record<string, () => SVGGElement> = {
    selected: () => this.seatMap.nativeElement.querySelector('.seat-selected-template')?.cloneNode(true),
    unavailable: () => this.seatMap.nativeElement.querySelector('.seat-unavailable-template')?.cloneNode(true),
  };

  scrollEvents$: Subject<number> = new Subject<number>();
  readonly scrollDebounceTime = 200;

  constructor(private gtmService: GtmService) {}

  ngAfterViewInit(): void {
    this.setWrapperWidth();
    this.activePax$ = this.passengers$.pipe(
      map((passengers) => {
        return passengers.find((passenger) => passenger.active);
      }),
      distinctUntilChanged((prev, curr) => {
        return prev.id === curr.id;
      })
    );

    /* Whenever there's a change in passengers, we will mark seats with correct templates (unavailable, selected) for all passengers. */
    this.subscriptions.add(
      this.passengers$
        .pipe(distinctUntilChanged(compareAsJson), withLatestFrom(this.flight.seatMap$.pipe()))
        .subscribe(([passengers, seatMapSvgAndControlData]) => {
          this.resetSeatView(passengers[0].id);
          passengers.forEach((passenger) => {
            if (passenger.active) {
              this.markSeatsUnavailableForPax(passenger.id);
              this.bringSelectedSeatTemplateOnTop(passenger.id);
              if (passenger.bundleActive) {
                this.markSeatsWithoutBundlesUnavailable(passenger.id, seatMapSvgAndControlData);
              }
            }

            if (passenger.seatNumber) {
              this.addSelectedSeatTemplateToPax(passenger);
            }

            if (passenger.originalSeatNumber) {
              this.addOriginalSeatForPax(passenger);
            }
          });
        })
    );

    this.subscriptions.add(
      this.scrollPositionWatcher$.pipe(distinctUntilChanged()).subscribe((position) => {
        this.updatePosition.emit(position);
      })
    );

    this.subscriptions.add(
      this.scrollEvents$.pipe(debounceTime(this.scrollDebounceTime)).subscribe((position) => {
        this.updateScrollPosition.emit(position);
        this.gtmService.trackElement(
          'seatmap-full',
          GaContext.SEAT_MAP,
          ElementTypes.BUTTON,
          '',
          ElementActions.SCROLL
        );
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  addOriginalSeatForPax(passenger: SeatMapTraveler): void {
    const seatToSelect = this.seatMap.nativeElement.querySelector(`[data-id="${passenger.originalSeatNumber}"]`);
    seatToSelect.classList.add('current-seat');
  }

  markSeatsUnavailableForPax(paxId: string): void {
    this.seatMap.nativeElement.querySelectorAll(`[data-${paxId.toLowerCase()}="unavailable"]`).forEach((seat) => {
      const seatLink = seat.querySelector('a');
      const template = this.templates.unavailable();
      const originalAriaLabel = seatLink.getAttribute('aria-label');
      const seatNumber = seat.getAttribute('data-id');
      const occupiedSeat = seat.classList.contains('seat-occupied');

      seatLink.setAttribute('aria-label', `${seatNumber}, ${this.unavailableSeatLabel}`);
      seatLink.setAttribute('data-original-aria-label', originalAriaLabel);

      if (!occupiedSeat) {
        template.classList.remove('seat-unavailable-template');
        template.classList.add('seat-unavailable');
        seatLink.appendChild(template);
      }
    });
  }

  markSeatsWithoutBundlesUnavailable(paxId: string, seatMapSvgAndControlData: SeatmapSvgAndControlData): void {
    this.seatMap.nativeElement.querySelectorAll(`[data-${paxId.toLowerCase()}]`).forEach((seat) => {
      const seatNumber = seat.getAttribute('data-id');

      if (this.paxHasNoBundleAvailabilityForSeat(paxId, seatNumber, seatMapSvgAndControlData)) {
        const seatLink = seat.querySelector('a');
        const originalAriaLabel = seatLink.getAttribute('aria-label');

        seatLink.setAttribute('aria-label', `${seatNumber}, ${this.unavailableSeatLabel}`);
        seatLink.setAttribute('data-original-aria-label', originalAriaLabel);

        const template = this.templates.unavailable();
        template.classList.remove('seat-unavailable-template');
        template.classList.add('seat-unavailable');
        seatLink.appendChild(template);
      }
    });
  }

  addSelectedSeatTemplateToPax(passenger: SeatMapTraveler): void {
    const initials = getInitials(passenger.traveler.firstName, passenger.traveler.lastName);
    const template = this.templates.selected();
    const seatToSelect = this.seatMap.nativeElement.querySelector(`[data-id="${passenger.seatNumber}"] a`);

    const ariaLabel = seatToSelect.getAttribute('aria-label');
    seatToSelect.setAttribute(
      'aria-label',
      `${ariaLabel}. ${passenger.traveler.firstName} ${passenger.traveler.lastName}`
    );

    template.classList.remove('seat-selected-template');
    template.classList.add('seat-selected');
    template.querySelector('.pax-label').innerHTML = initials;

    const codeLabel = template.querySelector('.code-label');
    codeLabel.innerHTML = passenger.seatNumber;
    if (passenger.bundleActive && passenger.bundleCategory) {
      codeLabel.setAttribute('x', '18');
      template.appendChild(this.createBundleIcon(passenger.bundleCategory));
    }
    seatToSelect.appendChild(template);
  }

  bringSelectedSeatTemplateOnTop(paxId: string): void {
    /*  The seat-selected template is added before seat-unavailable template in the DOM.
    Thus it's not visible when changing to a pax with an infant, because we mark certain seats unavailable.
    We need to bring the selected seat template on top in the DOM. */
    this.seatMap?.nativeElement?.querySelectorAll(`[data-${paxId.toLowerCase()}="unavailable"]`).forEach((seat) => {
      const seatLink = seat.querySelector('a');
      const selectedSeat = seatLink?.querySelector('.seat-selected');

      if (selectedSeat) {
        seatLink.removeChild(selectedSeat); // first remove selectedSeat template from underneath unavailable seat template
        seatLink.appendChild(selectedSeat);
      }
    });
  }

  removeSeatSelection(passengerId: string, originalSeatNumber: string = null): void {
    this.selectSeat.emit({
      passengerId,
      seatNumber: originalSeatNumber,
      exitRow: false,
    });
  }

  resetSeatView(paxId: string): void {
    const rootSelector = `[data-${paxId.toLowerCase()}]`;
    const selectedOrUnavailable = `${rootSelector} .seat-selected, ${rootSelector} .seat-unavailable`;

    this.seatMap.nativeElement.querySelectorAll(selectedOrUnavailable).forEach((node) => {
      if (node) {
        const originalAriaLabel = node.parentNode.getAttribute('data-original-aria-label');
        const ariaLabel = node.parentNode.getAttribute('aria-label');

        if (originalAriaLabel) {
          node.parentNode.setAttribute('aria-label', originalAriaLabel);
          node.parentNode.removeAttribute('data-original-aria-label');
        }

        node.parentNode.setAttribute('aria-label', ariaLabel?.split('.')[0]);
        node.parentNode.removeChild(node);
      }
    });
  }

  handleSeatClick(event: MouseEvent): void {
    event.preventDefault();
    const seatElement = (event.target as Element).closest('.seat');
    if (!seatElement) {
      return;
    }

    this.subscriptions.add(
      this.activePax$
        .pipe(take(1), withLatestFrom(this.passengers$, this.flight.seatMap$))
        .subscribe(([activePax, passengers, seatMap]) => {
          const isAvailableForPax = seatElement?.getAttribute(`data-${activePax.id.toLowerCase()}`) === 'available';
          const seatId = seatElement.getAttribute('data-id');
          const isExitRow = Boolean(seatElement.getAttribute('data-exitrow'));
          const currentSelectedSeat = passengers.find((pax) => pax.seatNumber === seatId);

          if (!isAvailableForPax && seatId !== activePax.seatNumber && seatId !== activePax.originalSeatNumber) {
            this.trackSeatSelection('seat-unavailable', seatElement, activePax);
            return;
          }

          if (this.isIncludedForPassenger(activePax.id, seatId) && seatId !== activePax.originalSeatNumber) {
            return;
          }

          if (activePax.bundleActive && this.paxHasNoBundleAvailabilityForSeat(activePax.id, seatId, seatMap)) {
            return;
          }

          if (!currentSelectedSeat || (activePax.seatNumber === seatId && activePax.originalSeatNumber)) {
            this.selectSeat.emit({
              passengerId: activePax.id,
              seatNumber: !currentSelectedSeat ? seatId : activePax.originalSeatNumber,
              exitRow: isExitRow,
            });
            this.trackSeatSelection('seat-selected', seatElement, activePax);
            return;
          }

          this.trackSeatSelection('seat-cleared', seatElement, activePax);
          this.removeSeatSelection(currentSelectedSeat.id, currentSelectedSeat.originalSeatNumber);
        })
    );
  }

  handleScroll(): void {
    const seatMapElement = this.seatMapWrapper.nativeElement;
    const divWidth = seatMapElement.clientWidth;
    const seatMapWidth = seatMapElement.scrollWidth;
    const scrollLeft = Math.round(seatMapElement.scrollLeft); // Use rounding to get rid of half pixels
    this.scrollEvents$.next(scrollLeft);

    const isAtStart = scrollLeft === 0;
    const isAtEnd = scrollLeft === seatMapWidth - divWidth;
    const value = isAtStart ? 'left' : isAtEnd ? 'right' : 'center';

    this.scrollPositionWatcher$.next(value);
  }

  setWrapperWidth(): void {
    const svg = this.seatMap.nativeElement.querySelector('svg');
    const svgWidth = svg?.getAttribute('data-optimal-width');
    if (svgWidth) {
      this.seatMap.nativeElement.setAttribute('style', `width: ${svgWidth}px`);
    }
  }

  private trackSeatSelection(
    id: 'seat-selected' | 'seat-cleared' | 'seat-unavailable',
    seatElement: Element,
    activePax: SeatMapTraveler
  ): void {
    this.subscriptions.add(
      this.flight.seatMap$.pipe(take(1)).subscribe((seatMap) => {
        this.gtmService.seatMapEvent(id, ElementTypes.BUTTON, 'cta', ElementActions.CLICK, {
          seatInfo: getSeatInfoForTracking(seatMap, seatElement, activePax),
          flightInfo: getSeatMapFlight(this.flight),
        });
      })
    );
  }

  private isIncludedForPassenger(passengerId: string, seatId: string): boolean {
    return servicesForFlightAndTravelerId(this.includedSeats, this.flight.id, passengerId).some(
      ({ seatNumber }) => seatNumber === seatId
    );
  }

  private paxHasNoBundleAvailabilityForSeat(
    passengerId: string,
    seatNumber: string,
    seatMap: SeatmapSvgAndControlData
  ): boolean {
    return !(Object.values(seatMap?.bundleServiceCatalogItem?.services ?? {})[0]?.[passengerId] ?? []).some((bundle) =>
      bundle.parameters?.seatNumbers?.includes(seatNumber)
    );
  }

  private createBundleIcon(bundleCategory: Category): SVGElement {
    const bundleIcon = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    bundleIcon.setAttribute('href', `#${DEFAULT_ICON_LIBRARY}/${getIconForServiceCategory(bundleCategory)}`);
    bundleIcon.setAttribute('x', '29');
    bundleIcon.setAttribute('y', '24');
    bundleIcon.setAttribute('width', '16');
    bundleIcon.setAttribute('height', '16');
    bundleIcon.setAttribute('class', 'nordic-blue-900-text');
    return bundleIcon;
  }
}
